<template>
  <div v-if="filteredOptions.length" class="option-section mb-3">
    <div class="d-flex align-items-center mb-3">
      <component :is="iconComponent" class="me-2" />
      <h5 class="body-strong mb-0">
        {{ titleParts.bold }}<span class="caption">{{ titleParts.regular }}</span>
      </h5>
    </div>
    <div
      v-for="(item, index) in filteredOptions"
      :key="index"
      class="mb-2"
    >
      <!-- Flight Details -->
      <FlightDetails 
        v-if="item.familyId === FamilyIds.FLIGHT && item.tripInformation && hasFlightDetails(item.tripInformation)"
        :tripInformation="item.tripInformation"
      />
      
      <!-- Train Details (for train packages) -->
      <TrainDetails 
        v-else-if="(item.familyId === FamilyIds.TRAIN || item.familyId === FamilyIds.TRAIN_ALTERNATIVE) && item.tripInformation && hasTrainDetails(item.tripInformation)"
        :tripInformation="item.tripInformation"
      />
      
      <!-- Transfer Details (for all packages) -->
      <TransferDetails 
        v-else-if="item.familyId === FamilyIds.TRANSFER && resolvedTransferTrip(item).length && hasTransferDetails(resolvedTransferTrip(item))"
        :tripInformation="resolvedTransferTrip(item)"
      />
      
      <!-- Luggage Details (only show if tripInformation exist and is array) -->
      <div v-else-if="item.familyId === FamilyIds.LUGGAGE && item.tripInformation && Array.isArray(item.tripInformation) && item.tripInformation.length > 0" class="luggage-details">
        <div class="luggage-detail-info">
          <p class="mb-0 caption">
            × {{ item.quantity || 1 }} {{ luggageDisplayLabel(item) }}
          </p>
        </div>
      </div>
      
      <!-- Default display for all services (including those without details) -->
      <div v-else class="default-service">
        <p class="mb-0 caption">
          {{ item.nameCommercial }}
          <span class="caption-strong" v-if="item.quantity && item.quantity > 1">x{{ item.quantity }}</span>
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { defineProps, computed } from 'vue'
  import { useStore } from 'vuex'
  import { useI18n } from 'vue-i18n'
  import { Service } from '@/services/CatalogService.interface'
  import { FamilyIds } from '@/assets/families'
  import {ServiceType} from "@/config"
  import FlightDetails from './FlightDetails.vue'
  import TrainDetails from './TrainDetails.vue'
  import TransferDetails from './TransferDetails.vue'
  import { BookingSummary } from '@/services/BookingService.interface'

  import { useSectionVisibilty } from '@/composables/useSectionVisibilty'
  import {
    dedupeTransferOptionsForDisplay,
    resolveTripInfoForOption,
    transferTypeFromOptionName,
  } from '@/utils/transferTripInformation'
  import {
    getLuggageCoverage,
    getLuggageDetails,
    isLuggageLeafSelected,
    resolveLuggageTripInfo,
    type LuggageOptionLike,
  } from '@/utils/luggageTripInformation'

  // Define the props with types
  const props = defineProps<{
    options: Partial<Service>[] // Array of Option objects
    sectionTitle: string // Title of the section
    iconComponent: object // Vue component for the icon
  }>()

  const store = useStore()
  const { t } = useI18n()

  const toLuggageOption = (item: Partial<Service>): LuggageOptionLike => ({
    optionId: item.optionId,
    id: item.id,
    nameCommercial: item.nameCommercial,
    sellingPrice: item.sellingPrice,
    quantity: item.quantity,
    familyId: item.familyId,
    tripInformation: item.tripInformation,
  })

  const luggageSiblings = computed(() =>
    props.options
      .filter(option => option.familyId === FamilyIds.LUGGAGE)
      .map(toLuggageOption)
  )

  const luggageDisplayLabel = (item: Partial<Service>): string => {
    const matched = resolveLuggageTripInfo(toLuggageOption(item), luggageSiblings.value)
    const details = getLuggageDetails(matched)
    const description =
      (typeof details?.description === 'string' && details.description) ||
      item.nameCommercial ||
      t('reservations.luggage')
    const weight = details?.weight
    const coverage = getLuggageCoverage(details)
    const coverageLabel =
      coverage === 'both-ways'
        ? t('reservations.luggageBothWays')
        : coverage === 'outbound-only'
          ? t('reservations.luggageOutboundOnly')
          : coverage === 'inbound-only'
            ? t('reservations.luggageInboundOnly')
            : ''
    if (weight != null && weight !== '') {
      return coverageLabel
        ? `${description}: ${weight}kg (${coverageLabel})`
        : `${description}: ${weight}kg`
    }
    return item.nameCommercial || String(description)
  }

  const transferTypeLabels = computed(() => ({
    bus: t('reservations.bus'),
    taxi: t('reservations.taxi'),
    funicular: t('reservations.funicular'),
  }))

  const resolvedTransferTrip = (item: Partial<Service>) =>
    resolveTripInfoForOption(
      {
        prestId: item.id,
        familyId: item.familyId,
        name: item.nameCommercial,
      },
      item.tripInformation
    )

  const getResolvedTransferType = (item: Partial<Service>) => {
    const resolved = resolvedTransferTrip(item)
    if (resolved.length) {
      return getTransferType(resolved)
    }
    return (
      transferTypeFromOptionName(item.nameCommercial, transferTypeLabels.value) ??
      t('reservations.transfer')
    )
  }

  // Get dynamic section title with transfer type
  const dynamicSectionTitle = computed(() => {
    // Change title for transfer sections based on transfer type
    if (props.sectionTitle.toLowerCase().includes('transfer')) {
      const transferOption = filteredOptions.value.find(option => option.familyId === FamilyIds.TRANSFER)
      if (transferOption) {
        const transferType = getResolvedTransferType(transferOption)
        if (transferType && transferType !== 'Transfer') {
          return transferType
        }
      }
    }
    return props.sectionTitle
  })

  // Get title parts for all sections (bold and regular text)
  const titleParts = computed(() => {
    // Handle transfer sections
    if (props.sectionTitle.toLowerCase().includes('transfer')) {
      const transferOption = filteredOptions.value.find(option => option.familyId === FamilyIds.TRANSFER)
      if (transferOption) {
        const transferType = getResolvedTransferType(transferOption)
        if (transferType && transferType !== t('reservations.transfer')) {
          // Check if the transfer type contains "(Service Hours 24/7)"
          if (transferType.includes(`(${t('reservations.serviceHours24')})`)) {
            const baseType = transferType.replace(` (${t('reservations.serviceHours24')})`, '')
            return {
              bold: baseType,
              regular: ` (${t('reservations.serviceHours24')})`
            }
          }
          return {
            bold: transferType,
            regular: ''
          }
        }
      }
    }
    
    // Handle train sections
    if (props.sectionTitle.toLowerCase().includes('train')) {
      // For now, just use the translated train title
      // Could be extended later to show specific train types if needed
      return {
        bold: t('reservations.train'),
        regular: ''
      }
    }
    
    // Handle flight sections
    if (props.sectionTitle.toLowerCase().includes('flight')) {
      // For now, just use the translated flight title
      // Could be extended later to show specific flight types if needed
      return {
        bold: t('reservations.flight'),
        regular: ''
      }
    }
    
    // Default case - use the provided section title (which should already be translated)
    return {
      bold: props.sectionTitle,
      regular: ''
    }
  })
  
  const selectedBooking = computed<BookingSummary | null>(
    () => store.getters['booking/selectedBooking']
  )

  const {showAccommodationOption} = useSectionVisibilty(selectedBooking.value?.liheId || 0, selectedBooking.value?.liheName, selectedBooking.value?.packageCode)

  // Filter out services with 'Sans' in nameCommercial and unselected luggage
  const filteredOptions = computed(() => {
    const filtered = props.options.filter(option => {
      console.log("option: ",)
      if(option.serviceType === ServiceType.ACCOMMODATION && !showAccommodationOption.value) {
        return false
      } 

      // Filter out services with 'Sans' in nameCommercial
      if (!option.nameCommercial || option.nameCommercial.toLowerCase().includes('sans')) {
        return false
      }
      
      if (option.familyId === FamilyIds.LUGGAGE && option.tripInformation) {
        const matched = resolveLuggageTripInfo(
          toLuggageOption(option),
          luggageSiblings.value
        )
        if (matched && !isLuggageLeafSelected(matched)) {
          return false
        }
      }
      
      return true
    })
    return dedupeTransferOptionsForDisplay(filtered)
  })

  // Simple validation functions for child components
  const hasFlightDetails = (tripInformation: any) => {
    console.log('hasFlightDetails called with:', tripInformation)
    
    if (Array.isArray(tripInformation) && tripInformation.length > 0) {
      // Check for the correct structure: tripInformation[0].details
      const flightDetail = tripInformation[0].details || tripInformation[0].tripInformation
      console.log('Flight detail from array:', flightDetail)
      const hasDetails = flightDetail && (flightDetail.outboundTrip || flightDetail.inboundTrip || flightDetail.class)
      console.log('Has flight details:', hasDetails)
      return hasDetails
    }
    
    console.log('Flight detail from direct object:', tripInformation)
    const hasDetails = tripInformation && (tripInformation.outboundTrip || tripInformation.inboundTrip || tripInformation.class)
    console.log('Has flight details:', hasDetails)
    return hasDetails
  }

  const hasTrainDetails = (tripInformation: any) => {
    if (Array.isArray(tripInformation) && tripInformation.length > 0) {
      // Check for the correct structure: tripInformation[0].details
      const trainDetail = tripInformation[0].details || tripInformation[0].tripInformation
      return trainDetail && (trainDetail.outboundTrip || trainDetail.inboundTrip || trainDetail.class)
    }
    return tripInformation && (tripInformation.outboundTrip || tripInformation.inboundTrip || tripInformation.class)
  }

  const hasTransferDetails = (tripInformation: any) => {
    if (Array.isArray(tripInformation) && tripInformation.length > 0) {
      // Check for the correct structure: tripInformation[0].details
      const transferDetail = tripInformation[0].details || tripInformation[0].tripInformation
      // Check for taxi transfer structure (has transferType and time ranges)
      if (transferDetail && transferDetail.transferType && 
          (transferDetail.outboundHours || transferDetail.inboundHours)) {
        return true
      }
      // Check for bus transfer structure with references
      if (transferDetail && (transferDetail.transferReturnRef || transferDetail.transferDepartureRef)) {
        return true
      }
      // Fallback to original structure
      return transferDetail && (transferDetail.departInfo || transferDetail.arriveeInfo)
    }
    // Check direct object for taxi transfers
    if (tripInformation && tripInformation.transferType && (tripInformation.outboundHours || tripInformation.inboundHours)) {
      return true
    }
    // Check direct object for bus transfers
    if (tripInformation && (tripInformation.transferReturnRef || tripInformation.transferDepartureRef)) {
      return true
    }
    return tripInformation && (tripInformation.departInfo || tripInformation.arriveeInfo)
  }

  // Helper function to check if service hours indicate 24/7
  const is24HourService = (hours: any) => {
    if (!hours) return false
    if (Array.isArray(hours)) return false
    
    // Check if the time indicates 24/7 service (00:01 or 23:59)
    return (hours.from === '00:01' && hours.to === '23:59') || 
           (hours.from === '23:59' && hours.to === '00:01')
  }

  // Get transfer type from tripInformation
  const getTransferType = (tripInformation: any) => {
    let transferType = t('reservations.transfer')
    let is24Hours = false
    
    if (Array.isArray(tripInformation) && tripInformation.length > 0) {
      // Check for the correct structure: tripInformation[0].details
      const transferDetail = tripInformation[0].details || tripInformation[0].tripInformation
      if (transferDetail?.transferType === 'TAXI') {
        transferType = t('reservations.taxi')
        // Check for 24/7 service hours
        is24Hours = is24HourService(transferDetail.outboundHours) || is24HourService(transferDetail.inboundHours)
      } else if (transferDetail?.transferType === 'BUS') {
        transferType = t('reservations.bus')
      } else if (transferDetail?.transferType === 'FUNICULAR') {
        transferType = t('reservations.funicular')
      } else if (transferDetail?.outboundHours && !Array.isArray(transferDetail.outboundHours) && (transferDetail.outboundHours as any).provider === 'FUNICULAR') {
        transferType = t('reservations.funicular')
      } else if (transferDetail?.inboundHours && !Array.isArray(transferDetail.inboundHours) && (transferDetail.inboundHours as any).provider === 'FUNICULAR') {
        transferType = t('reservations.funicular')
      } else if (transferDetail?.outboundHours && !Array.isArray(transferDetail.outboundHours) && 'provider' in transferDetail.outboundHours) {
        transferType = transferDetail.outboundHours.provider
      }
    } else {
      if (tripInformation?.transferType === 'TAXI') {
        transferType = t('reservations.taxi')
        // Check for 24/7 service hours
        is24Hours = is24HourService(tripInformation.outboundHours) || is24HourService(tripInformation.inboundHours)
      } else if (tripInformation?.transferType === 'BUS') {
        transferType = t('reservations.bus')
      } else if (tripInformation?.transferType === 'FUNICULAR') {
        transferType = t('reservations.funicular')
      } else if (tripInformation?.outboundHours && !Array.isArray(tripInformation.outboundHours) && (tripInformation.outboundHours as any).provider === 'FUNICULAR') {
        transferType = t('reservations.funicular')
      } else if (tripInformation?.inboundHours && !Array.isArray(tripInformation.inboundHours) && (tripInformation.inboundHours as any).provider === 'FUNICULAR') {
        transferType = t('reservations.funicular')
      } else if (tripInformation?.outboundHours && !Array.isArray(tripInformation.outboundHours) && 'provider' in tripInformation.outboundHours) {
        transferType = tripInformation.outboundHours.provider
      }
    }
    
    // Return transfer type with 24/7 indicator if applicable
    return is24Hours ? `${transferType} (${t('reservations.serviceHours24')})` : transferType
  }
</script>

<style scoped>
  .option-section {
    border-bottom: 1px solid #e0e0e0;
    padding-bottom: 0.5rem;
  }

  .luggage-details, .default-service {
    padding: 0.25rem 0;
  }

  .caption-strong {
    font-weight: 600;
  }
</style>
